/**
 * Furthest-step tracker.
 *
 * Tracks the deepest onboarding milestone the user has reached so that we
 * can bucket returning users into the Quiz Abandoner Menu audiences directly
 * (`beforeMealPrep` / `afterMealPrep` / `atCheckout`)
 *
 * `createFurthestStepTracker` lets another flow (e.g. Flat Rate) keep its own
 * tracker keyed to its own cart cookie and localStorage prefix. The named
 * exports below are /onboarding's instance.
 */

import Cookies from 'js-cookie';
import { MILESTONES, QUIZ_MILESTONES, STATE } from './constants';

const CART_COOKIE_NAME = 'shopifyCartId';
const STORAGE_KEY_PREFIX = 'furthestStep:';

const EXCLUDED_MILESTONES = new Set([STATE.initial, STATE.submitCart]);

const STATE_BY_PATH = Object.fromEntries(
  Object.entries(STATE).map(([key, path]) => [path, key]),
);

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage;

/**
 * @param {object} config
 * @param {string} config.cartCookieName cookie holding the flow's cart id
 * @param {string} config.storageKeyPrefix localStorage key prefix; must be
 *   unique per flow, since stale-entry cleanup clears every key under it
 * @param {string[]} [config.milestones] ordered milestone keys
 * @param {string[]} [config.quizMilestones] milestones bucketed as `beforeMealPrep`
 */
export const createFurthestStepTracker = ({
  cartCookieName,
  storageKeyPrefix,
  milestones = MILESTONES,
  quizMilestones = QUIZ_MILESTONES,
}) => {
  const milestoneIndex = milestone => milestones.indexOf(milestone);

  const getStorageKey = cartId => `${storageKeyPrefix}${cartId}`;

  const getCartIdFromCookie = () => {
    try {
      return Cookies.get(cartCookieName) ?? null;
    } catch {
      return null;
    }
  };

  /**
   * Clean up localStorage entries with `${storageKeyPrefix}*` prefix
   *
   * exceptCartId: optionl prop used when writing the current
   * cart's marker so we don't delete then rewrite the same key
   */
  const clearStaleFurthestStepEntries = (exceptCartId = null) => {
    if (!isBrowser()) return;
    try {
      const exceptKey = exceptCartId ? getStorageKey(exceptCartId) : null;
      const keysToRemove = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(storageKeyPrefix) && key !== exceptKey) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => window.localStorage.removeItem(key));
    } catch {}
  };

  const readFurthestStep = () => {
    if (!isBrowser()) return null;

    const cartId = getCartIdFromCookie();
    if (!cartId) return null;

    let raw;
    try {
      raw = window.localStorage.getItem(getStorageKey(cartId));
    } catch {
      return null;
    }
    if (!raw) return null;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      try {
        window.localStorage.removeItem(getStorageKey(cartId));
      } catch {}
      return null;
    }

    if (!parsed || typeof parsed.step !== 'string') return null;
    if (!milestones.includes(parsed.step)) return null;

    return parsed.step;
  };

  /**
   * Writes `step` if it's at or beyond the currently stored milestone.
   */
  const writeFurthestStep = step => {
    if (!isBrowser()) return;
    if (!milestones.includes(step)) return;

    const cartId = getCartIdFromCookie();
    if (!cartId) return;

    const current = readFurthestStep();
    if (current && milestoneIndex(current) >= milestoneIndex(step)) return;

    const storageKey = getStorageKey(cartId);

    try {
      if (!window.localStorage.getItem(storageKey)) {
        clearStaleFurthestStepEntries(cartId);
      }
      window.localStorage.setItem(storageKey, JSON.stringify({ step }));
    } catch {}
  };

  const clearFurthestStep = () => clearStaleFurthestStepEntries();

  /**
   * Forcibly resets the furthest-step milestone to `step`. Use this when a
   * previously-reached milestone is no longer actually true -- e.g. an
   * allergy edit clears a pet's `starter_box`.
   */
  const resetFurthestStepTo = step => {
    if (!isBrowser()) return;
    if (!milestones.includes(step)) return;

    const cartId = getCartIdFromCookie();
    if (!cartId) return;

    const storageKey = getStorageKey(cartId);

    try {
      if (!window.localStorage.getItem(storageKey)) {
        clearStaleFurthestStepEntries(cartId);
      }
      window.localStorage.setItem(storageKey, JSON.stringify({ step }));
    } catch {}
  };

  /**
   * Map a state machine state name to a milestone id.
   * Interstitials are not milestones; attribute them to `fromQuestion` when present.
   */
  const milestoneFromStateName = (stateName, { fromQuestion } = {}) => {
    if (!stateName || typeof stateName !== 'string') return null;
    if (EXCLUDED_MILESTONES.has(stateName)) return null;

    if (stateName === STATE.interstitial) {
      return fromQuestion && milestones.includes(fromQuestion)
        ? fromQuestion
        : null;
    }

    const key = STATE_BY_PATH[stateName];

    return key && milestones.includes(key) ? key : null;
  };

  /**
   * Map a furthest-step milestone to the bucket used by the QuizAbandonerMenu.
   */
  const bucketFromFurthestStep = step => {
    if (!step) return null;
    if (step === 'intro' || quizMilestones.includes(step))
      return 'beforeMealPrep';
    if (step === 'checkout') return 'atCheckout';
    return 'afterMealPrep';
  };

  return {
    readFurthestStep,
    writeFurthestStep,
    clearFurthestStep,
    resetFurthestStepTo,
    milestoneFromStateName,
    bucketFromFurthestStep,
  };
};

export const {
  readFurthestStep,
  writeFurthestStep,
  clearFurthestStep,
  resetFurthestStepTo,
  milestoneFromStateName,
  bucketFromFurthestStep,
} = createFurthestStepTracker({
  cartCookieName: CART_COOKIE_NAME,
  storageKeyPrefix: STORAGE_KEY_PREFIX,
});

export const FURTHEST_STEP_STORAGE_KEY_PREFIX = STORAGE_KEY_PREFIX;
export const FURTHEST_STEP_COOKIE_KEY = CART_COOKIE_NAME;
